<script setup>
// Staged bulk importer (hybrid model). Deck-level metadata mirrors the manual
// deck creator as PAGE FIELDS (Deck name, Category, Tags, Disclaimer) and acts
// as defaults; the CSV carries the questions (minimum question + answer; header
// optional). Optional domain/subject/topic/deck COLUMNS override the defaults
// per row, so one file can still create multiple decks.
//
// Mandatory staging stage: editable preview + validation + structure summary +
// AI-spend estimate + new-vs-existing taxonomy. Nothing is sent until Confirm.
// On confirm → POST /api/library/import (server re-validates).
//
// The preview is a Tabulator grid (the same library as LibraryItemsGrid.vue):
// compact, sortable, inline-editable, with low-priority columns folding into a
// per-row responsive-collapse detail, an Issues column, and a focused edit
// modal — so a 300-row import is readable without a 1200px horizontal scroll.
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from "vue";
import { TabulatorFull as Tabulator } from "tabulator-tables";
import "tabulator-tables/dist/css/tabulator_bootstrap5.min.css";
import { parseImportCsv, validateMappedItem, mergeDefaults, summariseImport, TEMPLATE_COLUMNS } from "../../lib/parseImport.js";
import { xlsxToCsv, isSpreadsheet } from "../../lib/readXlsx.js";
import { useAuthStore } from "@digitalstreams/saas-core/vue/stores/authStore";
import { useRoute } from "vue-router";

const auth = useAuthStore();
const route = useRoute();
// Curate mode (route /curate — meta.curate, super-admin only): every confirm
// seeds straight to the PUBLIC/homepage catalog (bypasses the row cap + content
// review, generates audio, auto-publishes each deck when baked). The normal
// /library/import route is always a plain private import — no admin path there.
const isCurate = computed(() => route.meta?.curate === true && auth.isAdmin);

// The downloadable template is a physical file served by Express at
// /templates/import-template.csv. TEMPLATE_COLUMNS (imported from parseImport)
// is the single source of truth for the column set + the file's header row.
const TEMPLATE_URL = "/templates/import-template.csv";

// --- deck-level page fields (mirror the manual creator) --------------------
const deckName = ref("");
const tagsText = ref("");
const disclaimer = ref("");
const description = ref("");
const byline = ref("");

// --- category picker (Domain → Subject → Topic) + inline create -----------
const categoryTree = ref([]);
const idIndex = ref(new Map());
const selDomain = ref("");
const selSubject = ref("");
const selTopic = ref("");
const subjectOpts = computed(() => idIndex.value.get(selDomain.value)?.node.children || []);
const topicOpts = computed(() => idIndex.value.get(selSubject.value)?.node.children || []);
const creating = ref(null);
const newName = ref("");
const createBusy = ref(false);
const createError = ref("");

function indexTree(nodes, map) { for (const n of nodes) { map.set(n.id, { node: n }); if (n.children?.length) indexTree(n.children, map); } }
async function loadCategories() {
  try {
    const r = await fetch("/api/categories", { credentials: "include" });
    categoryTree.value = r.ok ? (await r.json()).categories || [] : [];
    const m = new Map(); indexTree(categoryTree.value, m); idIndex.value = m;
  } catch { /* picker just shows no options */ }
}
function onSelectLevel(level) { creating.value = null; if (level === "domain") { selSubject.value = ""; selTopic.value = ""; } if (level === "subject") selTopic.value = ""; }
function startCreate(level) { creating.value = level; newName.value = ""; createError.value = ""; }
async function createCategory(level) {
  const name = newName.value.trim();
  if (!name) return;
  const parentId = level === "domain" ? null : level === "subject" ? selDomain.value : selSubject.value;
  createBusy.value = true; createError.value = "";
  try {
    const r = await fetch("/api/categories", {
      method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, level, parentId: parentId || undefined })
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data.error || "Could not create the category.");
    await loadCategories();
    if (level === "domain") selDomain.value = data.category.id;
    else if (level === "subject") selSubject.value = data.category.id;
    else selTopic.value = data.category.id;
    creating.value = null; newName.value = "";
  } catch (e) { createError.value = e.message; } finally { createBusy.value = false; }
}

const defaults = computed(() => ({
  deckName: deckName.value.trim(),
  domain: idIndex.value.get(selDomain.value)?.node.name || "",
  subject: idIndex.value.get(selSubject.value)?.node.name || "",
  topic: idIndex.value.get(selTopic.value)?.node.name || "",
  tags: tagsText.value.split(",").map((t) => t.trim()).filter(Boolean),
  disclaimer: disclaimer.value.trim(),
  description: description.value.trim(),
  byline: byline.value.trim()
}));

// --- file / preview --------------------------------------------------------
const rawText = ref("");
const fileName = ref("");
const items = ref([]);
const unknownColumns = ref([]);
const headerless = ref(false);
const parsed = ref(false);
const committing = ref(false);
const commitError = ref("");
const result = ref(null);
const fileInput = ref(null);
const dragOver = ref(false);
const fileError = ref("");

// Apply the page-field defaults to clones (the editable grid keeps the raw CSV
// values; the summary + the import payload use the effective, merged rows).
function effectiveItems() {
  const clones = items.value.map((it) => ({ ...it, distractors: [...it.distractors] }));
  return mergeDefaults(clones, defaults.value);
}
const summary = computed(() => parsed.value ? summariseImport(effectiveItems(), categoryTree.value) : null);

function ingest(text) {
  const out = parseImportCsv(text);
  items.value = out.items;
  unknownColumns.value = out.unknownColumns;
  headerless.value = out.headerless;
  parsed.value = true;
  result.value = null;
  commitError.value = "";
}
const isCsv = (file) => /\.csv$/i.test(file.name) || /csv|text\/plain/.test(file.type || "");
async function readFile(file) {
  fileError.value = "";
  if (!file) return;
  fileName.value = file.name;
  // .xlsx/.xls → convert the first sheet to CSV (SheetJS, lazy-loaded), then
  // feed the same CSV pipeline. .csv → read as text directly.
  if (isSpreadsheet(file.name)) {
    try {
      const csv = await xlsxToCsv(await file.arrayBuffer());
      if (!csv.trim()) { fileError.value = "That spreadsheet looks empty."; return; }
      rawText.value = csv; ingest(csv);
    } catch {
      fileError.value = "Could not read that spreadsheet — try saving it as .csv.";
    }
    return;
  }
  if (!isCsv(file)) { fileError.value = `"${file.name}" isn't a .csv or .xlsx file.`; return; }
  const reader = new FileReader();
  reader.onerror = () => { fileError.value = "Could not read that file."; };
  reader.onload = () => { rawText.value = String(reader.result || ""); ingest(rawText.value); };
  reader.readAsText(file);
}
function onFile(e) { readFile(e.target.files?.[0]); }
function onDrop(e) { dragOver.value = false; readFile(e.dataTransfer?.files?.[0]); }
function parsePasted() { if (rawText.value.trim()) ingest(rawText.value); }
function reset() { stopProgress(); parsed.value = false; items.value = []; unknownColumns.value = []; rawText.value = ""; fileName.value = ""; fileError.value = ""; result.value = null; }

// --- Tabulator preview grid -------------------------------------------------
const gridEl = ref(null);
let table = null;

// One grid row per item; _idx links back to items.value for write-back.
function gridRows() {
  return items.value.map((it, idx) => ({
    _idx: idx, n: idx + 1,
    question: it.question, answer: it.answer,
    d1: it.distractors[0], d2: it.distractors[1], d3: it.distractors[2],
    difficulty: it.difficulty, type: it.type, tags: it.tags,
    deck: it.deck, domain: it.domain, subject: it.subject, topic: it.topic,
    _problems: it.problems
  }));
}
const autoFmt = (cell) => cell.getValue() ? cell.getValue() : '<span class="text-muted fst-italic">auto</span>';
const defFmt = (cell) => cell.getValue() ? cell.getValue() : '<span class="text-muted fst-italic">(default)</span>';
const issueFmt = (cell) => {
  const p = cell.getValue();
  if (Array.isArray(p) && p.length) {
    const tip = p.join("; ").replace(/"/g, "&quot;");
    return `<span class="text-danger" title="${tip}"><i class="bi bi-exclamation-circle-fill"></i> ${p.length}</span>`;
  }
  return '<span class="text-success"><i class="bi bi-check-circle"></i></span>';
};

// Push a single edited cell back into items.value, revalidate, refresh the row's
// Issues marker. Keeps items.value the source of truth so the reactive summary
// (rows-to-fix, deck count) updates as you edit.
function syncCell(cell) {
  const data = cell.getRow().getData();
  const it = items.value[data._idx];
  if (!it) return;
  const f = cell.getField();
  if (f === "d1") it.distractors[0] = data.d1;
  else if (f === "d2") it.distractors[1] = data.d2;
  else if (f === "d3") it.distractors[2] = data.d3;
  else it[f] = data[f];
  validateMappedItem(it);
  cell.getRow().update({ _problems: it.problems });
}

function buildGrid() {
  if (table || !gridEl.value) return;
  table = new Tabulator(gridEl.value, {
    data: gridRows(),
    layout: "fitColumns",
    responsiveLayout: "collapse",          // low-priority columns fold into a per-row detail
    index: "_idx",
    height: "60vh",                        // virtual scroll — 300 rows stay fast
    placeholder: "No rows",
    rowFormatter(row) {
      const d = row.getData();
      row.getElement().classList.toggle("row-problem", Array.isArray(d._problems) && d._problems.length > 0);
    },
    columns: [
      { formatter: "responsiveCollapse", width: 34, hozAlign: "center", resizable: false, headerSort: false },
      { title: "#", field: "n", width: 46, hozAlign: "right", headerSort: false, editor: false, responsive: 0 },
      { title: "Issues", field: "_problems", width: 70, hozAlign: "center", headerSort: false, editor: false, responsive: 0, formatter: issueFmt },
      { title: "Question", field: "question", editor: "textarea", widthGrow: 3, minWidth: 220, responsive: 0, headerFilter: "input" },
      { title: "Answer", field: "answer", editor: "textarea", widthGrow: 2, minWidth: 160, responsive: 0 },
      { title: "Diff", field: "difficulty", editor: "input", width: 60, hozAlign: "center", responsive: 5 },
      { title: "D1", field: "d1", editor: "input", widthGrow: 1, minWidth: 110, responsive: 6, formatter: autoFmt },
      { title: "D2", field: "d2", editor: "input", widthGrow: 1, minWidth: 110, responsive: 7, formatter: autoFmt },
      { title: "D3", field: "d3", editor: "input", widthGrow: 1, minWidth: 110, responsive: 8, formatter: autoFmt },
      { title: "Type", field: "type", editor: "input", width: 90, responsive: 9 },
      { title: "Tags", field: "tags", editor: "input", width: 120, responsive: 9 },
      { title: "Deck", field: "deck", editor: "input", width: 150, responsive: 10, formatter: defFmt },
      { title: "Domain", field: "domain", editor: "input", width: 130, responsive: 11, formatter: defFmt },
      { title: "Subject", field: "subject", editor: "input", width: 130, responsive: 12, formatter: defFmt },
      { title: "Topic", field: "topic", editor: "input", width: 150, responsive: 13, formatter: defFmt },
      { title: "", width: 40, hozAlign: "center", headerSort: false, resizable: false, responsive: 0,
        formatter: () => '<i class="bi bi-pencil-square text-primary"></i>',
        cellClick: (_e, cell) => openDetail(cell.getRow().getData()._idx) }
    ]
  });
  table.on("cellEdited", syncCell);
}
function destroyGrid() { if (table) { table.destroy(); table = null; } }
watch(parsed, async (v) => { if (v) { await nextTick(); buildGrid(); } else destroyGrid(); });
onBeforeUnmount(destroyGrid);

// --- per-row edit modal -----------------------------------------------------
const editingIdx = ref(null);
const editing = computed(() => editingIdx.value == null ? null : items.value[editingIdx.value]);
function openDetail(idx) { editingIdx.value = idx; }
function revalidateEditing() { if (editing.value) validateMappedItem(editing.value); }
function closeDetail() {
  if (editingIdx.value != null && table) {
    const it = items.value[editingIdx.value];
    validateMappedItem(it);
    table.updateData([{
      _idx: editingIdx.value, question: it.question, answer: it.answer,
      d1: it.distractors[0], d2: it.distractors[1], d3: it.distractors[2],
      difficulty: it.difficulty, type: it.type, tags: it.tags,
      deck: it.deck, domain: it.domain, subject: it.subject, topic: it.topic, _problems: it.problems
    }]);
  }
  editingIdx.value = null;
}

async function confirmImport() {
  commitError.value = "";
  committing.value = true;
  try {
    const rows = effectiveItems().map((it) => ({
      deck: it.deck, domain: it.domain, subject: it.subject, topic: it.topic,
      question: it.question, answer: it.answer, distractors: it.distractors,
      difficulty: it.difficulty, type: it.type, tags: it.tags,
      explanation: it.explanation, memoryHint: it.memoryHint, citation: it.citation,
      description: it.description, byline: it.byline,
      deckTags: it.deckTags, disclaimer: it.disclaimer
    }));
    const url = isCurate.value ? "/api/admin/seed-decks" : "/api/library/import";
    const r = await fetch(url, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ rows })
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data.error || "Import couldn't complete — please try again.");
    result.value = data;
    result.value.seeded = isCurate.value;
    startProgress((data.decks || []).map((d) => d.id));
  } catch (e) {
    console.error("[import] confirm failed:", e);
    commitError.value = e.message;
  } finally {
    committing.value = false;
  }
}

// --- live post-import progress ---------------------------------------------
// After confirm, poll GET /api/library/decks and follow each created deck through
// its pipeline (scaffold → generate → ready/public), with a rough ETA from the
// audio-clip bake rate. Shared by curate + normal import.
const liveById = ref({});   // deckId -> summary
const samples = ref([]);    // {t, done} clip samples for ETA
let progressTimer = null;

const liveList = computed(() => (result.value?.decks || []).map((d) => ({ ...d, live: liveById.value[d.id] || null })));
const deckClips = (d) => (d.itemCount || 0) * 7;
// Terminal for the automatic pipeline: draft (normal import ends here), ready,
// error. Seed keeps going draft→ready→public via the auto-publish flag.
const TERMINAL = ["draft", "ready", "error"];
// Per-deck completion fraction (0–1) so the overall bar moves smoothly through
// scaffold → generate rather than jumping per deck.
function deckFrac(d) {
  const s = d.live?.status;
  if (!s) return 0;
  if (TERMINAL.includes(s)) return 1;
  if (s === "generating") { const g = d.live.generation || {}; return g.total ? (g.done || 0) / g.total : 0.5; }
  if (s === "queued") return 0.05;
  const sc = d.live.scaffold || {}; return sc.total ? 0.3 * ((sc.done || 0) / sc.total) : 0.1; // scaffolding
}
const overall = computed(() => {
  const decks = liveList.value;
  const total = decks.length;
  const totalClips = decks.reduce((n, d) => n + deckClips(d), 0);
  const doneClips = decks.reduce((n, d) => n + (d.live?.status === "ready" ? deckClips(d) : (d.live?.generation?.done || 0)), 0);
  const frac = decks.reduce((n, d) => n + deckFrac(d), 0);
  return {
    total,
    ready: decks.filter((d) => d.live?.status === "ready").length,
    published: decks.filter((d) => d.live?.visibility === "public").length,
    imported: decks.filter((d) => TERMINAL.includes(d.live?.status)).length,
    errored: decks.filter((d) => d.live?.status === "error").length,
    totalClips, doneClips,
    pct: total ? Math.round((100 * frac) / total) : 0,
    done: total > 0 && decks.every((d) => TERMINAL.includes(d.live?.status))
  };
});
const etaText = computed(() => {
  const s = samples.value;
  if (s.length < 2 || overall.value.done) return null;
  const a = s[0], b = s[s.length - 1];
  const dDone = b.done - a.done, dT = (b.t - a.t) / 1000;
  if (dDone <= 0 || dT <= 0) return null;
  const remaining = overall.value.totalClips - overall.value.doneClips;
  if (remaining <= 0) return null;
  const secs = remaining / (dDone / dT);
  return secs < 90 ? "less than a minute" : `about ${Math.ceil(secs / 60)} min`;
});
function deckPhase(d) {
  const s = d.live?.status;
  if (!s) return { label: "Queued", cls: "bg-secondary", detail: "" };
  if (s === "scaffold_queued" || s === "scaffolding") {
    const sc = d.live.scaffold || {};
    return { label: "Writing options", cls: "bg-info text-dark", detail: sc.total ? `${sc.done || 0}/${sc.total} questions` : "" };
  }
  if (s === "queued") return { label: "Queued for audio", cls: "bg-info text-dark", detail: "" };
  if (s === "generating") {
    const g = d.live.generation || {};
    return { label: "Recording audio", cls: "bg-warning text-dark", detail: g.total ? `${g.done || 0}/${g.total} clips` : "" };
  }
  if (s === "draft") return { label: "Draft", cls: "bg-secondary", detail: "ready to generate" };
  if (s === "ready") return d.live.visibility === "public"
    ? { label: "Live on homepage", cls: "bg-success", detail: "" }
    : { label: "Ready", cls: "bg-success", detail: "" };
  if (s === "error") return { label: "Error", cls: "bg-danger", detail: d.live.generation?.error || d.live.scaffold?.error || "" };
  return { label: s, cls: "bg-secondary", detail: "" };
}
async function pollProgress(ids) {
  try {
    const r = await fetch("/api/library/decks", { credentials: "include" });
    if (!r.ok) return;
    const all = (await r.json()).decks || [];
    const map = {};
    for (const d of all) if (ids.includes(d.id)) map[d.id] = d;
    liveById.value = map;
    samples.value.push({ t: Date.now(), done: overall.value.doneClips });
    if (samples.value.length > 6) samples.value.shift();
    if (overall.value.done) stopProgress();
  } catch { /* transient — next tick retries */ }
}
function startProgress(ids) {
  stopProgress();
  liveById.value = {}; samples.value = [];
  if (!ids.length) return;
  pollProgress(ids);
  progressTimer = setInterval(() => pollProgress(ids), 5000);
}
function stopProgress() { if (progressTimer) { clearInterval(progressTimer); progressTimer = null; } }

onBeforeUnmount(stopProgress);
onMounted(loadCategories);
</script>

<template>
  <div class="container-fluid p-0">
    <router-link :to="isCurate ? '/home' : '/library'" class="btn btn-sm btn-link px-0 mb-2"><i class="bi bi-arrow-left me-1"></i>{{ isCurate ? "Home" : "My Library" }}</router-link>
    <h1 class="h3 mb-1">{{ isCurate ? "Curate public content" : "Import decks from CSV" }}</h1>

    <!-- Curate mode: unmistakable framing that this publishes site-wide. -->
    <div v-if="isCurate" class="alert alert-primary d-flex align-items-start gap-2 py-2">
      <i class="bi bi-stars fs-5"></i>
      <div class="small">
        <strong>Super-admin — public homepage.</strong> Decks you seed here are published to the <strong>public catalog every visitor sees</strong>. This skips the row cap and content review, generates audio, and auto-publishes each deck when it's baked. For your own private decks, use <router-link to="/library/import">My Library → Import CSV</router-link> instead.
      </div>
    </div>

    <!-- Result -->
    <div v-if="result" class="card border-0 shadow-sm mb-3">
      <div class="card-body">
        <h2 class="h5 mb-2">
          <i class="bi me-2" :class="overall.done ? 'bi-check-circle text-success' : 'bi-arrow-repeat text-primary'"></i>
          {{ overall.done ? (result.seeded ? "Published" : "Imported") : (result.seeded ? "Publishing" : "Importing") }}
          {{ overall.total }} deck(s) · {{ result.summary.itemCount }} question(s)
        </h2>

        <!-- Overall progress + ETA -->
        <div class="d-flex justify-content-between small text-muted mb-1">
          <span>
            <template v-if="result.seeded"><strong class="text-success">{{ overall.published }}</strong> live</template>
            <template v-else><strong>{{ overall.ready }}</strong> ready</template>
            of {{ overall.total }}
            <span v-if="overall.errored" class="text-danger">· {{ overall.errored }} error</span>
          </span>
          <span v-if="!overall.done && etaText">≈ {{ etaText }} remaining</span>
          <span v-else-if="overall.done" class="text-success">Done</span>
        </div>
        <div class="progress mb-3" style="height:8px">
          <div class="progress-bar" :class="overall.done ? 'bg-success' : 'progress-bar-striped progress-bar-animated'" :style="{ width: overall.pct + '%' }"></div>
        </div>

        <p v-if="!overall.done" class="text-muted small mb-2">
          <span class="spinner-border spinner-border-sm me-1"></span>
          Generating in the background — <strong>you can leave this page; it keeps running.</strong>
          <span v-if="result.seeded"> Decks appear on the public catalog as each one finishes.</span>
        </p>
        <p v-else class="small mb-2" :class="overall.errored ? 'text-warning' : 'text-success'">
          <i class="bi bi-check2-all me-1"></i>
          {{ result.seeded ? `${overall.published} deck(s) are live for visitors.` : `${overall.imported} deck(s) imported to your library — open a deck to generate its audio.` }}
          <span v-if="overall.errored">{{ overall.errored }} need a look (open the deck to retry).</span>
        </p>

        <!-- Per-deck live status -->
        <div style="max-height:340px; overflow-y:auto" class="border rounded">
          <div v-for="d in liveList" :key="d.id" class="d-flex align-items-center gap-2 px-2 py-1 border-bottom small">
            <div class="flex-grow-1 text-truncate">
              <router-link :to="`/library/${d.id}`" class="text-decoration-none">{{ d.topic }}</router-link>
              <span class="text-muted ms-1">{{ d.itemCount }}q</span>
            </div>
            <span class="text-muted" style="min-width:6rem; text-align:right">{{ deckPhase(d).detail }}</span>
            <span class="badge" :class="deckPhase(d).cls" style="min-width:8rem">{{ deckPhase(d).label }}</span>
          </div>
        </div>

        <div class="d-flex gap-2 mt-3">
          <router-link v-if="result.seeded" to="/topics" class="btn btn-primary btn-sm">View public catalog</router-link>
          <router-link v-else to="/library" class="btn btn-primary btn-sm">Go to My Library</router-link>
          <button class="btn btn-outline-secondary btn-sm" @click="reset">{{ result.seeded ? "Seed another file" : "Import another file" }}</button>
        </div>
      </div>
    </div>

    <template v-else>
      <!-- Curate: one-line note + template download only. No lengthy how-it-works,
           no deck-detail defaults — the uploaded file's columns define everything. -->
      <div v-if="isCurate" class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
        <p class="text-muted small mb-0">Upload a <strong>.csv</strong> or <strong>.xlsx</strong> — the file's columns (<code>deck, domain, subject, topic, question, answer, distractor1–3</code>) define everything.</p>
        <a class="btn btn-sm btn-outline-primary" :href="TEMPLATE_URL" download="i-recall-import-template.csv"><i class="bi bi-download me-1"></i>Download template</a>
      </div>

      <!-- Instructions (full) — normal import only -->
      <div v-if="!isCurate" class="card border-0 shadow-sm mb-3">
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-start flex-wrap gap-2">
            <h2 class="h6 mb-2"><i class="bi bi-info-circle me-2"></i>How it works</h2>
            <a class="btn btn-sm btn-outline-primary" :href="TEMPLATE_URL" download="i-recall-import-template.csv"><i class="bi bi-download me-1"></i>Download CSV template</a>
          </div>
          <ol class="small mb-2 ps-3">
            <li>Set the deck details below (same fields as building a deck by hand). These apply to every question unless your file overrides them.</li>
            <li>Upload a <strong>.csv or .xlsx</strong> file (or paste CSV text). The <strong>minimum is two columns — question and answer</strong>; a header row is optional.</li>
            <li>Leave the three distractors blank to have them generated, or supply your own. Optional columns: <code>difficulty</code> (1–5), <code>type</code>, <code>tags</code>.</li>
            <li>To create <strong>several decks from one file</strong>, add <code>domain</code>, <code>subject</code>, <code>topic</code> and/or <code>deck</code> columns — rows sharing a <code>deck</code> name become one deck.</li>
            <li>Review &amp; fix the preview, then <strong>Confirm import</strong>. Nothing is created (and no AI is used) until you confirm.</li>
          </ol>
          <p class="text-muted small mb-0">Full column reference: <code>{{ TEMPLATE_COLUMNS.join(", ") }}</code>.</p>
        </div>
      </div>

      <!-- Deck details (page fields = defaults; mirror the manual creator).
           Hidden in curate mode — root files carry their own deck/taxonomy. -->
      <div v-if="!isCurate" class="card border-0 shadow-sm mb-3">
        <div class="card-body">
          <h2 class="h6 mb-3">Deck details <span class="text-muted small fw-normal">— defaults for every question (a file with its own <code>deck</code>/<code>domain</code> columns overrides these)</span></h2>
          <div class="row g-3">
            <div class="col-12 col-md-6">
              <label class="form-label small text-muted">Deck name</label>
              <input v-model="deckName" type="text" class="form-control" placeholder="e.g. Respiratory Basics">
            </div>
            <div class="col-12 col-md-6">
              <label class="form-label small text-muted">Tags <span class="text-muted">— comma-separated, optional</span></label>
              <input v-model="tagsText" type="text" class="form-control" placeholder="exam, year-1">
            </div>
            <div class="col-12 col-md-8">
              <label class="form-label small text-muted">Description <span class="text-muted">— a short line for the deck cover + library, optional</span></label>
              <input v-model="description" type="text" class="form-control" placeholder="e.g. Core respiratory concepts for beginners.">
            </div>
            <div class="col-12 col-md-4">
              <label class="form-label small text-muted">Byline <span class="text-muted">— attribution, optional</span></label>
              <input v-model="byline" type="text" class="form-control" placeholder="e.g. Meat &amp; Livestock Australia">
            </div>
            <div class="col-12">
              <label class="form-label small text-muted">Category <span class="text-muted">— where the deck(s) appear in Topics</span></label>
              <div class="row g-2">
                <div class="col-12 col-md-4">
                  <label class="form-label small text-muted mb-1">Domain</label>
                  <div class="input-group input-group-sm">
                    <select v-model="selDomain" @change="onSelectLevel('domain')" class="form-select">
                      <option value="">Choose…</option>
                      <option v-for="d in categoryTree" :key="d.id" :value="d.id">{{ d.name }}</option>
                    </select>
                    <button type="button" class="btn btn-outline-secondary" title="Add a domain" @click="startCreate('domain')"><i class="bi bi-plus-lg"></i></button>
                  </div>
                </div>
                <div class="col-12 col-md-4">
                  <label class="form-label small text-muted mb-1">Subject</label>
                  <div class="input-group input-group-sm">
                    <select v-model="selSubject" @change="onSelectLevel('subject')" class="form-select" :disabled="!selDomain">
                      <option value="">Choose…</option>
                      <option v-for="s in subjectOpts" :key="s.id" :value="s.id">{{ s.name }}</option>
                    </select>
                    <button type="button" class="btn btn-outline-secondary" title="Add a subject" :disabled="!selDomain" @click="startCreate('subject')"><i class="bi bi-plus-lg"></i></button>
                  </div>
                </div>
                <div class="col-12 col-md-4">
                  <label class="form-label small text-muted mb-1">Topic</label>
                  <div class="input-group input-group-sm">
                    <select v-model="selTopic" @change="onSelectLevel('topic')" class="form-select" :disabled="!selSubject">
                      <option value="">Choose…</option>
                      <option v-for="t in topicOpts" :key="t.id" :value="t.id">{{ t.name }}</option>
                    </select>
                    <button type="button" class="btn btn-outline-secondary" title="Add a topic" :disabled="!selSubject" @click="startCreate('topic')"><i class="bi bi-plus-lg"></i></button>
                  </div>
                </div>
              </div>
              <div v-if="creating" class="d-flex flex-wrap gap-2 align-items-center mt-2 p-2 bg-light rounded">
                <span class="small text-muted text-capitalize">New {{ creating }}:</span>
                <input v-model="newName" type="text" class="form-control form-control-sm" style="max-width:240px" :placeholder="`${creating} name`" @keyup.enter.prevent="createCategory(creating)">
                <button type="button" class="btn btn-sm btn-primary" :disabled="createBusy || !newName.trim()" @click="createCategory(creating)"><span v-if="createBusy" class="spinner-border spinner-border-sm me-1"></span>Add</button>
                <button type="button" class="btn btn-sm btn-link text-decoration-none" @click="creating = null">Cancel</button>
              </div>
              <div v-if="createError" class="text-danger small mt-1">{{ createError }}</div>
            </div>
            <div class="col-12">
              <label class="form-label small text-muted">Disclaimer <span class="text-muted">— optional</span></label>
              <input v-model="disclaimer" type="text" class="form-control" placeholder="Illustrative content — not professionally validated.">
            </div>
          </div>
        </div>
      </div>

      <!-- Source -->
      <div v-if="!parsed" class="card border-0 shadow-sm mb-3">
        <div class="card-body">
          <div class="mb-3">
            <label class="form-label small text-muted d-block">Upload a .csv or .xlsx file</label>
            <div
              class="dropzone text-center p-4 rounded border border-2"
              :class="dragOver ? 'border-primary bg-primary-subtle' : 'border-secondary-subtle'"
              role="button" tabindex="0"
              @click="fileInput?.click()"
              @keyup.enter="fileInput?.click()"
              @dragover.prevent="dragOver = true"
              @dragenter.prevent="dragOver = true"
              @dragleave.prevent="dragOver = false"
              @drop.prevent="onDrop"
            >
              <i class="bi bi-cloud-arrow-up fs-3 d-block mb-1 text-secondary"></i>
              <div class="small"><strong>Drag &amp; drop</strong> a .csv or .xlsx here, or <span class="text-primary text-decoration-underline">browse</span></div>
              <div v-if="fileName" class="small text-muted mt-1"><i class="bi bi-file-earmark-text me-1"></i>{{ fileName }}</div>
              <input ref="fileInput" type="file" accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" class="d-none" @change="onFile">
            </div>
            <div v-if="fileError" class="text-danger small mt-1"><i class="bi bi-exclamation-triangle me-1"></i>{{ fileError }}</div>
          </div>
          <div class="text-center text-muted small my-2">— or paste CSV text —</div>
          <textarea v-model="rawText" class="form-control font-monospace small" rows="6" placeholder="What is the primary function of the lungs?,Gas exchange"></textarea>
          <button class="btn btn-primary mt-2" :disabled="!rawText.trim()" @click="parsePasted">Preview</button>
        </div>
      </div>

      <!-- Review + correct -->
      <template v-else>
        <div class="card border-0 shadow-sm mb-3">
          <div class="card-body">
            <div class="row text-center g-2 mb-2">
              <div class="col"><div class="h4 mb-0">{{ summary.totalRows }}</div><div class="small text-muted">rows</div></div>
              <div class="col"><div class="h4 mb-0">{{ summary.deckCount }}</div><div class="small text-muted">decks</div></div>
              <div class="col"><div class="h4 mb-0">{{ summary.domainCount }}</div><div class="small text-muted">domains</div></div>
              <div class="col"><div class="h4 mb-0" :class="summary.generationCount ? 'text-primary' : ''">{{ summary.generationCount }}</div><div class="small text-muted">AI generations</div></div>
              <div class="col"><div class="h4 mb-0" :class="summary.problemRows ? 'text-danger' : 'text-success'">{{ summary.problemRows }}</div><div class="small text-muted">rows to fix</div></div>
            </div>
            <div v-if="headerless" class="alert alert-info py-2 small mb-2">No header row detected — reading columns positionally as <code>question, answer, distractor1–3, difficulty, type</code>.</div>
            <div v-if="unknownColumns.length" class="alert alert-warning py-2 small mb-2">Unrecognised column(s) ignored: <code>{{ unknownColumns.join(", ") }}</code>.</div>

            <p class="small mb-1"><strong>{{ summary.newNodeCount }}</strong> new taxonomy node(s) will be created:</p>
            <div class="mb-2">
              <span v-for="(n, i) in summary.newNodes" :key="i" class="badge bg-info-subtle text-dark border me-1 mb-1">{{ n.level }}: {{ n.name }}<span v-if="n.under" class="text-muted"> (under {{ n.under }})</span></span>
              <span v-if="!summary.newNodes.length" class="text-muted small">none — all map to existing nodes.</span>
            </div>

            <div class="d-flex gap-2 align-items-center flex-wrap">
              <button class="btn" :class="isCurate ? 'btn-success' : 'btn-primary'" :disabled="summary.blocking || committing" @click="confirmImport"><span v-if="committing" class="spinner-border spinner-border-sm me-2"></span>{{ isCurate ? "Seed to public homepage" : "Confirm import" }}</button>
              <button class="btn btn-outline-secondary" @click="reset">Start over</button>
              <span v-if="summary.blocking" class="text-danger small"><i class="bi bi-exclamation-triangle me-1"></i>Fix the highlighted rows first.</span>
            </div>
            <div v-if="commitError" class="alert alert-warning py-2 mt-2 mb-0">{{ commitError }}</div>
          </div>
        </div>

        <div class="card border-0 shadow-sm">
          <div class="card-body p-2 import-grid">
            <p class="text-muted small px-1 mb-2">
              Click a cell to edit, or the <i class="bi bi-pencil-square text-primary"></i> to open a row.
              Narrow columns fold into the <i class="bi bi-plus-circle"></i> row detail.
              Blank <code>deck/domain/subject/topic</code> inherit the Deck details above; blank distractors show <em>auto</em>.
            </p>
            <div ref="gridEl"></div>
          </div>
        </div>
        <p class="text-muted small mt-2">Blank distractors are generated after import (one AI call each); the deck then waits in Draft for content review + audio generation.</p>
      </template>
    </template>

    <!-- Per-row edit modal -->
    <div v-if="editing" class="ir-modal-backdrop" @click.self="closeDetail">
      <div class="ir-modal card shadow">
        <div class="card-header d-flex justify-content-between align-items-center py-2">
          <h2 class="h6 mb-0">Row {{ editingIdx + 1 }}</h2>
          <button class="btn-close" aria-label="Close" @click="closeDetail"></button>
        </div>
        <div class="card-body">
          <div v-if="editing.problems.length" class="alert alert-danger py-2 small mb-3">
            <strong>Needs fixing:</strong>
            <ul class="mb-0 mt-1 ps-3"><li v-for="(p, i) in editing.problems" :key="i">{{ p }}</li></ul>
          </div>
          <div class="mb-2">
            <label class="form-label small text-muted mb-1">Question</label>
            <textarea v-model="editing.question" rows="2" class="form-control form-control-sm" @input="revalidateEditing"></textarea>
          </div>
          <div class="mb-2">
            <label class="form-label small text-muted mb-1">Answer <span class="text-muted">(the correct option)</span></label>
            <textarea v-model="editing.answer" rows="2" class="form-control form-control-sm" @input="revalidateEditing"></textarea>
          </div>
          <div class="row g-2 mb-2">
            <div class="col-4"><label class="form-label small text-muted mb-1">Distractor 1</label><input v-model="editing.distractors[0]" class="form-control form-control-sm" placeholder="auto" @input="revalidateEditing"></div>
            <div class="col-4"><label class="form-label small text-muted mb-1">Distractor 2</label><input v-model="editing.distractors[1]" class="form-control form-control-sm" placeholder="auto" @input="revalidateEditing"></div>
            <div class="col-4"><label class="form-label small text-muted mb-1">Distractor 3</label><input v-model="editing.distractors[2]" class="form-control form-control-sm" placeholder="auto" @input="revalidateEditing"></div>
          </div>
          <div class="mb-2">
            <label class="form-label small text-muted mb-1">Explanation <span class="text-muted">— shown after answering</span></label>
            <textarea v-model="editing.explanation" rows="2" class="form-control form-control-sm"></textarea>
          </div>
          <div class="row g-2 mb-2">
            <div class="col-6"><label class="form-label small text-muted mb-1">Memory hint</label><input v-model="editing.memoryHint" class="form-control form-control-sm" placeholder="mnemonic / recall aid"></div>
            <div class="col-6"><label class="form-label small text-muted mb-1">Citation</label><input v-model="editing.citation" class="form-control form-control-sm" placeholder="reference / authority"></div>
          </div>
          <div class="row g-2 mb-2">
            <div class="col-4"><label class="form-label small text-muted mb-1">Difficulty</label><input v-model="editing.difficulty" class="form-control form-control-sm" placeholder="1–5" @input="revalidateEditing"></div>
            <div class="col-4"><label class="form-label small text-muted mb-1">Type</label><input v-model="editing.type" class="form-control form-control-sm" @input="revalidateEditing"></div>
            <div class="col-4"><label class="form-label small text-muted mb-1">Tags</label><input v-model="editing.tags" class="form-control form-control-sm" @input="revalidateEditing"></div>
          </div>
          <div class="row g-2">
            <div class="col-6 col-md-3"><label class="form-label small text-muted mb-1">Deck</label><input v-model="editing.deck" class="form-control form-control-sm" placeholder="(default)"></div>
            <div class="col-6 col-md-3"><label class="form-label small text-muted mb-1">Domain</label><input v-model="editing.domain" class="form-control form-control-sm" placeholder="(default)"></div>
            <div class="col-6 col-md-3"><label class="form-label small text-muted mb-1">Subject</label><input v-model="editing.subject" class="form-control form-control-sm" placeholder="(default)"></div>
            <div class="col-6 col-md-3"><label class="form-label small text-muted mb-1">Topic</label><input v-model="editing.topic" class="form-control form-control-sm" placeholder="(default)"></div>
          </div>
        </div>
        <div class="card-footer d-flex justify-content-end py-2">
          <button class="btn btn-sm btn-primary" @click="closeDetail">Done</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.dropzone {
  border-style: dashed !important;
  cursor: pointer;
  transition: background-color .15s ease, border-color .15s ease;
}
.dropzone:hover { border-color: var(--bs-primary); }

/* Compact, readable grid — smaller font + tighter rows than the default. */
.import-grid :deep(.tabulator) { font-size: .8rem; }
.import-grid :deep(.tabulator .tabulator-header) { font-size: .72rem; }
.import-grid :deep(.tabulator-cell) { padding: 3px 6px !important; }
.import-grid :deep(.tabulator-row.row-problem) { background: var(--bs-danger-bg-subtle); }
.import-grid :deep(.tabulator-row.row-problem .tabulator-cell) { border-color: var(--bs-danger-border-subtle); }

/* Lightweight modal (no Bootstrap JS dependency). */
.ir-modal-backdrop {
  position: fixed; inset: 0; z-index: 1080;
  background: rgba(0, 0, 0, .4);
  display: flex; align-items: flex-start; justify-content: center;
  padding: 4vh 1rem; overflow-y: auto;
}
.ir-modal { width: 100%; max-width: 680px; background: var(--bs-body-bg); }
</style>
